/**
 * One pillar and its sliver groups — a port of the generator's `pillarHtml`.
 *
 * The layout rule that looks arbitrary is not: a pillar with six or more sliver
 * groups splits them across two stacked `.p-body` rows, which roughly doubles
 * the room's height and halves its width. Without it the widest rooms in the
 * insurance plan scroll horizontally off the page and break the A3 print. The
 * threshold and the `Math.ceil` split are copied exactly, because changing
 * either changes where every wide room wraps.
 *
 * The weight is formatted as the generator formats it — `Math.round(w*1000)/10`
 * — which is NOT the same as the `fmtWeight` used on the room line. The two
 * differ in the original and the difference is visible, so both are preserved.
 */
import type { PillarModel } from '@blueprint/shared';
import { useTerms } from '../../lib/terms';
import { DeliverableChip } from './DeliverableChip';

/** The generator's pillar-weight formatting, verbatim: one decimal, trailing zeros dropped. */
function pillarWeight(weighting: number): string {
  return `${Math.round(weighting * 1000) / 10}%`;
}

function Head({ name, weighting }: { name: string; weighting: number }) {
  return (
    <div className="p-head">
      <span className="n">{name}</span>
      <span className="w">{pillarWeight(weighting)}</span>
    </div>
  );
}

export function Pillar({
  pillar,
  highlighted,
  onCycle,
}: {
  pillar: PillarModel;
  /** Indices (`__i`) of deliverables inside the highlighted week. */
  highlighted: ReadonlySet<number>;
  onCycle?: (index: number) => void;
}) {
  const terms = useTerms();

  // A pillar carrying PercentOverride and no deliverable detail shows the figure
  // instead of a breakdown. It is not clickable and carries no data-di.
  if (pillar.isOverride) {
    return (
      <div className="pillar">
        <Head name={pillar.name} weighting={pillar.weighting} />
        <div className="chips">
          <div className="chip pillar-override">
            <span className="dot" />
            <span className="t">
              {pillar.overridePct}% — summary figure, no {terms.noun('deliverable').toLowerCase()}{' '}
              breakdown extracted
            </span>
          </div>
        </div>
      </div>
    );
  }

  const groups = pillar.groups.map((group, index) => {
    const chips = group.deliverables.map((d) => (
      <DeliverableChip
        deliverable={d}
        highlighted={highlighted.has(d.__i ?? -1)}
        {...(onCycle ? { onCycle } : {})}
        key={d.__i ?? `${group.sliver}-${d.DeliverableName}`}
      />
    ));

    // A blank SliverName means the deliverables sit directly on the pillar.
    if (!group.sliver) {
      return (
        <div className="chips" key={`g${index}`}>
          {chips}
        </div>
      );
    }
    return (
      <div className="sliver-group" key={`g${index}`}>
        <div className="sliver-label">{group.sliver}</div>
        <div className="chips">{chips}</div>
      </div>
    );
  });

  let body;
  if (!groups.length) {
    body = (
      <div className="p-body">
        <div className="chips">
          <div className="chip s-draft">
            <span className="dot" />
            <span className="t">— none —</span>
          </div>
        </div>
      </div>
    );
  } else if (groups.length >= 6) {
    const half = Math.ceil(groups.length / 2);
    body = (
      <>
        <div className="p-body">{groups.slice(0, half)}</div>
        <div className="p-body">{groups.slice(half)}</div>
      </>
    );
  } else {
    body = <div className="p-body">{groups}</div>;
  }

  return (
    <div className="pillar">
      <Head name={pillar.name} weighting={pillar.weighting} />
      {body}
    </div>
  );
}
